import { Controller, FormProvider, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../stores/hooks/hooks.ts";
import { useEffect, useState } from "react";
import {
  editProfileData,
  fetchProfileData,
  fetchStates,
} from "../../../stores/thunks/profileThunk.tsx";
import {
  ProfileBodyType,
  ProfileDataType,
} from "../../../stores/types/profileTypes.tsx";
import { parsePhoneNumber, isValidPhoneNumber } from "react-phone-number-input";
import {
  RevActionsHeader,
  RevButton,
  RevCard,
  RevDragAndDrop,
  RevInput,
  RevPhoneInput,
  RevSelect,
  RevUnsavedChangesDialog,
} from "@revmatics/catalyst";
import { useUnsavedChangesDialog } from "../../../helpers/hooks/useUnsavedChangesDialog.tsx";
import { useNavigate } from "react-router";
import { useTrimmedRegister } from "../../../helpers/hooks/useTrimmedRegister.ts";
type Props = {
  profile: ProfileDataType;
};

export const ProfileSettingsPage = ({ profile }: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [isOpenUnsavedModal, setOpenUnsavedModal] = useState(false);
  const [country, setCountry] = useState<null | number>(null);
  const [state, setState] = useState<null | number>(null);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [href, setHref] = useState("");
  const [phoneNumberError, setPhoneNumberError] = useState("");
  const countries = useAppSelector((state) => state.profile.countries);
  const states = useAppSelector((state) => state.profile.states);
  const isLoadingEdit = useAppSelector((state) => state.profile.isLoadingEdit);
  const getFullData = (data: ProfileBodyType) => {
    return {
      ...data,
      country,
      state,
      phone_number: phoneNumber,
    };
  };

  useEffect(() => {
    if (!profile) {
      dispatch(fetchProfileData());
    }
  }, [dispatch, profile]);

  useEffect(() => {
    if (country) {
      dispatch(fetchStates(Number(country)));
    }
  }, [dispatch, country]);

  const methods = useForm<ProfileBodyType>({
    mode: "onSubmit",
    defaultValues: {
      ...profile,
      country: profile.country?.id,
      state: profile.state?.id,
    },
  });
  const {
    handleSubmit,
    control,
    register,
    getValues,
    reset,
    formState: { errors, isDirty },
  } = methods;

  useEffect(() => {
    if (profile) {
      reset({
        ...profile,
        country: profile.country?.id,
        state: profile.state?.id,
      });
      if (profile.country) {
        setCountry(Number(profile.country.id));
      }
      if (profile.state) {
        setState(Number(profile.state.id));
      }
      if (profile.phone_number) {
        setPhoneNumber(profile.phone_number);
      }
    }
  }, [profile, reset]);

  const hasUnsavedChanges =
    isDirty ||
    country !== (profile.country?.id ?? null) ||
    state !== (profile.state?.id ?? null) ||
    phoneNumber !== (profile.phone_number ?? "");

  useUnsavedChangesDialog({
    hasUnsavedChanges,
    onNavigateAttempt: (href) => {
      setHref(href);
      setOpenUnsavedModal(true);
    },
  });

  const openFileSystem = () => {
    const dndInput = document.querySelector(
      'input[name="profile_picture_dnd_input"]',
    );
    if (dndInput instanceof HTMLElement) {
      dndInput.click();
    } else {
      console.log(
        "Cannot programmatically open file dialog for RevDragAndDrop.",
      );
    }
  };

  const handleDiscardChanges = () => {
    setOpenUnsavedModal(false);
    if (href) {
      navigate(href);
    } else {
      navigate(-1);
    }
    setHref("");
  };

  const onSubmit = (data: ProfileBodyType) => {
    if (phoneNumber && !isValidPhoneNumber(phoneNumber)) {
      setPhoneNumberError("The phone number entered is not valid.");
      return;
    }
    const bodyData = getFullData(data);
    const formDataForApi = new FormData();

    (Object.keys(bodyData) as Array<keyof ProfileBodyType>).forEach((key) => {
      if (profile && key in profile) {
        if (bodyData[key] !== profile[key as keyof ProfileDataType]) {
          formDataForApi.append(
            key,
            bodyData[key]
              ? key === "profile_picture"
                ? bodyData[key]
                : (bodyData[key] as string | number | File).toString()
              : "",
          );
        }
      }
    });
    dispatch(
      editProfileData({
        body: formDataForApi,
        callback: () => {
          if (href) {
            navigate(href);
          }
        },
      }),
    ).finally(() => reset(data));
  };

  const registerTrimmed = useTrimmedRegister(register);

  if (countries) {
    return (
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="max-w-[754px] mx-auto space-y-6"
          noValidate
        >
          <RevActionsHeader title="Profile">
            <RevButton
              text="Save"
              type="submit"
              buttonSize="medium"
              isLoading={isLoadingEdit}
            />
          </RevActionsHeader>
          <RevCard
            paddingTitle="px-6 py-4"
            title="General"
            text="This is basic information about your account."
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <RevInput
                name="first_name"
                label="First Name"
                type="text"
                size="md"
                error={errors.first_name}
                register={registerTrimmed("first_name")}
                isLabelBold
              />
              <RevInput
                name="last_name"
                label="Last Name"
                type="text"
                size="md"
                error={errors.last_name}
                register={registerTrimmed("last_name")}
                isLabelBold
              />
            </div>
            <div className="mt-6">
              <label className="typography-body-bold-sm text-monochrome-900">
                Profile Image
              </label>
              <div className="grid grid-cols-[160px_1fr] gap-6 mt-2">
                <Controller
                  name="profile_picture"
                  control={control}
                  render={({
                    field: {
                      onChange: onRHFFileChange,
                      value: personaPictureFile,
                    },
                    fieldState,
                  }) => {
                    return (
                      <RevDragAndDrop
                        name="profile_picture_dnd_input"
                        onFileChange={(file) => {
                          onRHFFileChange(file);
                        }}
                        initialImageUrl={personaPictureFile}
                        error={fieldState.error}
                        hideRemoveButton={true}
                        dropzoneClassName="!h-[160px] !max-h-[160px]"
                      />
                    );
                  }}
                />
                <div className="space-y-4">
                  <RevButton
                    text="Add picture"
                    variant="secondary"
                    buttonSize="small"
                    handleClick={openFileSystem}
                  />
                  <p className="typography-body-xs text-monochrome-500">
                    Use SVG, PNG, JPG (max. 1MB). Best size: 200x200 pixels.
                  </p>
                </div>
              </div>
            </div>
          </RevCard>
          <RevCard
            paddingTitle="px-6 py-4"
            title="Contact"
            text="This is the personal information you use to access and manage your account. Your email and phone number are used for security and support."
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="relative">
                <RevInput
                  name="email"
                  label="Business Email"
                  type="text"
                  size="md"
                  disabled
                  register={register("email")}
                  isLabelBold
                />
              </div>
              <RevPhoneInput
                name="phone_number_input"
                label="Phone Number"
                isLabelBold
                value={phoneNumber}
                countryCode={
                  profile.phone_number
                    ? parsePhoneNumber(profile.phone_number)?.country
                    : undefined
                }
                onChange={(value) => setPhoneNumber(value)}
                error={phoneNumberError}
                setError={setPhoneNumberError}
              />
            </div>
            <p className="typography-body-bold-sm text-monochrome-900">
              Location
            </p>
            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-6">
              <RevSelect
                name="country"
                label="Country"
                options={countries.map(({ id, name }) => {
                  return { label: name, value: id.toString() };
                })}
                onChange={(value) => {
                  if (value) {
                    setCountry(Number(value));
                    setState(null);
                  }
                }}
                value={country ?? ""}
                size="md"
                placeholder="Select"
              />
              <RevSelect
                name="state"
                label="State"
                options={
                  states?.map(({ id, name }) => {
                    return { label: name, value: id.toString() };
                  }) || []
                }
                onChange={(value) => {
                  if (value) {
                    setState(Number(value));
                  }
                }}
                value={state ?? ""}
                size="md"
                placeholder="Select"
                disabled={!states || states.length === 0}
              />
            </div>
          </RevCard>
          <RevUnsavedChangesDialog
            pageId="profile-settings-page"
            isOpen={isOpenUnsavedModal}
            setIsOpen={setOpenUnsavedModal}
            onSave={() => onSubmit(getValues())}
            onDiscard={handleDiscardChanges}
            isLoading={isLoadingEdit}
          />
        </form>
      </FormProvider>
    );
  }
  return null;
};
